import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import Chart from '../../components/ui/Chart';
import { 
  BarChart3, 
  RefreshCw, 
  Filter, 
  FileText, 
  TrendingUp, 
  Users, 
  Clock, 
  AlertCircle,
  CheckCircle,
  Eye,
  Search,
  FileSpreadsheet,
  FileDown,
  Calendar,
  Settings,
  X,
  Plus,
  Edit,
  Trash2,
  PieChart,
  LineChart,
  Activity,
  Target,
  Zap,
  TrendingDown,
  Printer,
  Info,
  Shield
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';
import toast from 'react-hot-toast';

const Reports = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [scheduledReports, setScheduledReports] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('7d');
  const [reportType, setReportType] = useState('all');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [reportMetrics, setReportMetrics] = useState({});
  const [previewReport, setPreviewReport] = useState(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [scheduleReport, setScheduleReport] = useState(null);

  useEffect(() => {
    loadReports();
    loadScheduledReports();
  }, []);

  useEffect(() => {
    loadReportMetrics();
  }, [reports, scheduledReports]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const response = await MockDataService.getReports();
      if (response.success) {
        setReports(response.data || []);
      }
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const loadScheduledReports = async () => {
    try {
      // Mock scheduled reports
      setScheduledReports([
        { id: 1, name: 'Weekly Ticket Summary', frequency: 'weekly', nextRun: '2024-01-15', status: 'active' },
        { id: 2, name: 'Monthly Agent Performance', frequency: 'monthly', nextRun: '2024-02-01', status: 'active' }
      ]);
    } catch (error) {
      console.error('Error loading scheduled reports:', error);
    }
  };

  const loadReportMetrics = async () => {
    try {
      // Mock metrics data
      setReportMetrics({
        totalReports: reports.length || 45,
        completedToday: 8,
        inProgress: 3,
        scheduled: scheduledReports.length || 2,
        averageGenerationTime: '2.5 min'
      });
    } catch (error) {
      console.error('Error loading metrics:', error);
    }
  };

  const handleGenerateReport = async (type) => {
    setLoading(true);
    try {
      const response = await MockDataService.generateReport(type, dateRange);
      if (response.success) {
        toast.success('Report generated successfully');
        loadReports();
      } else {
        toast.error('Failed to generate report');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewReport = (reportId) => {
    const report = reports.find(r => r.id === reportId);
    if (!report) {
      toast.error('Report not found');
      return;
    }
    setPreviewReport(report);
    setIsPreviewModalOpen(true);
  };

  const handleDownloadReport = async (reportId, format = 'pdf') => {
    const report = reports.find(r => r.id === reportId);
    if (!report) {
      toast.error('Report not found');
      return;
    }
    if (report?.status !== 'completed') {
      toast.error('Report is still processing. Please try again once it is completed.');
      return;
    }
    toast.loading(`Preparing ${report.name} (${format.toUpperCase()})...`, { id: `download-${reportId}-${format}` });
    setTimeout(() => {
      toast.success(`${format.toUpperCase()} ready for ${report.name}`, { id: `download-${reportId}-${format}` });
    }, 1500);
  };

  const handleScheduleReport = (reportId) => {
    const report = reports.find(r => r.id === reportId);
    if (report) {
      setScheduleReport(report);
      setShowScheduleModal(true);
    } else {
      toast.error('Report not found');
    }
  };

  const handlePrintReport = (reportId) => {
    const report = reports.find(r => r.id === reportId);
    if (!report) {
      toast.error('Report not found');
      return;
    }
    if (report?.status !== 'completed') {
      toast.error('Only completed reports can be printed.');
      return;
    }
    toast.success(`Opening print dialog for ${report.name}`);
    window.print();
  };

  const handleExportSettings = () => {
    const settings = {
      dateRange,
      reportType,
      filters: { searchQuery }
    };
    const blob = new Blob([JSON.stringify(settings, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'report-settings.json';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Settings exported successfully');
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'tickets', label: 'Tickets', icon: FileText },
    { id: 'agents', label: 'Agents', icon: Users },
    { id: 'performance', label: 'Performance', icon: TrendingUp },
    { id: 'sla', label: 'SLA', icon: Target },
    { id: 'custom', label: 'Custom', icon: Filter },
    { id: 'scheduled', label: 'Scheduled', icon: Calendar }
  ];

  const reportTypes = [
    { value: 'all', label: 'All Reports' },
    { value: 'tickets', label: 'Ticket Reports' },
    { value: 'agents', label: 'Agent Reports' },
    { value: 'performance', label: 'Performance Reports' },
    { value: 'sla', label: 'SLA Reports' },
    { value: 'compliance', label: 'Compliance Reports' }
  ];

  const dateRanges = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: '1y', label: 'Last Year' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const filteredReports = reports.filter(report => {
    const matchesSearch = report?.name?.toLowerCase().includes(searchQuery?.toLowerCase() ?? '') ||
                         report?.description?.toLowerCase().includes(searchQuery?.toLowerCase() ?? '');
    const matchesType = reportType === 'all' || report?.type === reportType;
    return matchesSearch && matchesType;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'generating': return 'info';
      case 'failed': return 'destructive';
      case 'pending': return 'warning';
      default: return 'secondary';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'tickets': return FileText;
      case 'agents': return Users;
      case 'performance': return TrendingUp;
      case 'sla': return Clock;
      case 'compliance': return Shield;
      default: return BarChart3;
    }
  };

  // Mock chart data
  const ticketTrendsData = [
    { name: 'Mon', value: 45 },
    { name: 'Tue', value: 52 },
    { name: 'Wed', value: 48 },
    { name: 'Thu', value: 61 },
    { name: 'Fri', value: 55 },
    { name: 'Sat', value: 38 },
    { name: 'Sun', value: 32 }
  ];

  const statusDistributionData = [
    { name: 'Open', value: 125 },
    { name: 'In Progress', value: 89 },
    { name: 'Resolved', value: 234 },
    { name: 'Closed', value: 156 }
  ];

  const agentPerformanceData = [
    { name: 'Agent 1', value: 95 },
    { name: 'Agent 2', value: 87 },
    { name: 'Agent 3', value: 92 },
    { name: 'Agent 4', value: 78 },
    { name: 'Agent 5', value: 88 }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <BarChart3 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Reports & Analytics</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">Generate, view, and export comprehensive system reports</p>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handleExportSettings}
            className="flex items-center gap-2"
            title="Export report settings"
          >
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">Export Settings</span>
          </Button>
          <Button
            variant="outline"
            onClick={loadReports}
            disabled={loading}
            className="flex items-center gap-2"
            title="Refresh reports"
            aria-label="Refresh reports"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button
            onClick={() => {
              toast('Custom report builder coming soon!', { icon: 'ℹ️' });
            }}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
            title="Create new report"
          >
            <Plus className="h-4 w-4" />
            New Report
          </Button>
        </div>
      </div>

      {/* Toast Notification - Using react-hot-toast instead of inline message */}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <IconComponent className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Filters & Search */}
      <Card className="border-gray-200 dark:border-gray-700">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <Label htmlFor="report-search" className="sr-only">Search reports</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" aria-hidden="true" />
                <Input
                  id="report-search"
                  placeholder="Search reports by name or description..."
                  value={searchQuery ?? ''}
                  onChange={(e) => setSearchQuery(e?.target?.value ?? '')}
                  className="pl-10 w-full"
                  aria-label="Search reports"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <div className="w-full sm:w-48">
                <Label htmlFor="report-type-filter" className="sr-only">Filter by report type</Label>
                <Select
                  value={reportType}
                  onValueChange={setReportType}
                >
                  <SelectTrigger id="report-type-filter" className="w-full bg-white dark:bg-gray-800">
                    <SelectValue placeholder="All Reports">
                      {reportTypes.find(type => type.value === reportType)?.label || 'All Reports'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-gray-800">
                    {reportTypes.map((type) => (
                      <SelectItem 
                        key={type.value} 
                        value={type.value}
                        className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full sm:w-48">
                <Label htmlFor="date-range-filter" className="sr-only">Filter by date range</Label>
                <Select
                  value={dateRange}
                  onValueChange={setDateRange}
                >
                  <SelectTrigger id="date-range-filter" className="w-full bg-white dark:bg-gray-800">
                    <SelectValue placeholder="Date Range">
                      {dateRanges.find(range => range.value === dateRange)?.label || 'Last 7 Days'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-gray-800">
                    {dateRanges.map((range) => (
                      <SelectItem 
                        key={range.value} 
                        value={range.value}
                        className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Reports</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{reportMetrics.totalReports || reports.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Completed Today</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{reportMetrics.completedToday || 8}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                    <Clock className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">In Progress</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{reportMetrics.inProgress || 3}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <Calendar className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Scheduled</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{reportMetrics.scheduled || scheduledReports.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                    <Zap className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg. Time</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{reportMetrics.averageGenerationTime || '2.5 min'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Ticket Trends (Last 7 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Chart type="line" data={ticketTrendsData} height={300} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5" />
                  Status Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Chart type="pie" data={statusDistributionData} height={300} />
              </CardContent>
            </Card>
          </div>

          {/* Recent Reports */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Reports</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {filteredReports.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">
                      {searchQuery || reportType !== 'all'
                        ? 'No reports match your filters.'
                        : 'No reports available. Generate your first report to get started.'}
                    </p>
                  </div>
                ) : (
                  filteredReports.slice(0, 10).map((report) => {
                    const IconComponent = getTypeIcon(report?.type);
                    return (
                      <div key={report?.id || Math.random()} className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                        <div className="flex items-center gap-4 flex-1">
                          <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                            <IconComponent className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 dark:text-white">{report?.name || 'Untitled Report'}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{report?.description || 'No description'}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant={getStatusColor(report?.status)} size="sm">
                                {report?.status || 'unknown'}
                              </Badge>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {report?.createdAt ? new Date(report.createdAt).toLocaleDateString() : 'N/A'}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {report?.size || 'N/A'} KB
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewReport(report.id)}
                            title="Preview report"
                            aria-label={`Preview ${report?.name || 'report'}`}
                            className="hover:bg-blue-50 dark:hover:bg-blue-900/20"
                          >
                            <Eye className="h-4 w-4" />
                            <span className="sr-only">View</span>
                          </Button>
                          {report?.status === 'completed' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadReport(report.id, 'pdf')}
                                title="Download PDF"
                                aria-label={`Download ${report?.name || 'report'} as PDF`}
                                className="hover:bg-green-50 dark:hover:bg-green-900/20"
                                disabled={report?.status !== 'completed'}
                              >
                                <FileDown className="h-4 w-4" />
                                <span className="hidden sm:inline ml-1">PDF</span>
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadReport(report.id, 'csv')}
                                title="Download CSV"
                                aria-label={`Download ${report?.name || 'report'} as CSV`}
                                className="hover:bg-green-50 dark:hover:bg-green-900/20"
                                disabled={report?.status !== 'completed'}
                              >
                                <FileSpreadsheet className="h-4 w-4" />
                                <span className="hidden sm:inline ml-1">CSV</span>
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleScheduleReport(report.id)}
                                title="Schedule Report"
                                aria-label={`Schedule ${report?.name || 'report'}`}
                                className="hover:bg-purple-50 dark:hover:bg-purple-900/20"
                                disabled={report?.status !== 'completed'}
                              >
                                <Calendar className="h-4 w-4" />
                                <span className="hidden sm:inline ml-1">Schedule</span>
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePrintReport(report.id)}
                                title="Print Report"
                                aria-label={`Print ${report?.name || 'report'}`}
                                className="hover:bg-gray-50 dark:hover:bg-gray-800"
                                disabled={report?.status !== 'completed'}
                              >
                                <Printer className="h-4 w-4" />
                                <span className="sr-only">Print</span>
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tickets Tab */}
      {activeTab === 'tickets' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Ticket Reports
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { name: 'Ticket Summary', icon: FileText, type: 'ticket-summary' },
                  { name: 'Ticket Trends', icon: TrendingUp, type: 'ticket-trends' },
                  { name: 'Resolution Time', icon: Clock, type: 'ticket-resolution' },
                  { name: 'Ticket Volume', icon: BarChart3, type: 'ticket-volume' },
                  { name: 'Category Analysis', icon: PieChart, type: 'category-analysis' },
                  { name: 'Priority Distribution', icon: Target, type: 'priority-distribution' }
                ].map((report) => {
                  const Icon = report.icon;
                  return (
                    <Button
                      key={report.type}
                      variant="outline"
                      className="h-32 flex flex-col items-center justify-center gap-3 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      onClick={() => handleGenerateReport(report.type)}
                    >
                      <Icon className="h-8 w-8" />
                      <span className="text-sm font-medium">{report.name}</span>
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Agents Tab */}
      {activeTab === 'agents' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Agent Reports
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-6">
                <Chart type="bar" data={agentPerformanceData} height={250} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { name: 'Performance', icon: TrendingUp, type: 'agent-performance' },
                  { name: 'Workload', icon: BarChart3, type: 'agent-workload' },
                  { name: 'Satisfaction', icon: CheckCircle, type: 'agent-satisfaction' },
                  { name: 'Response Time', icon: Clock, type: 'agent-response-time' },
                  { name: 'Resolution Rate', icon: Target, type: 'agent-resolution-rate' },
                  { name: 'Activity Log', icon: Activity, type: 'agent-activity' }
                ].map((report) => {
                  const Icon = report.icon;
                  return (
                    <Button
                      key={report.type}
                      variant="outline"
                      className="h-32 flex flex-col items-center justify-center gap-3 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      onClick={() => handleGenerateReport(report.type)}
                    >
                      <Icon className="h-8 w-8" />
                      <span className="text-sm font-medium">{report.name}</span>
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Performance Tab */}
      {activeTab === 'performance' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Performance Reports
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { name: 'SLA Performance', icon: Clock, type: 'sla-performance' },
                  { name: 'Response Time', icon: BarChart3, type: 'response-time' },
                  { name: 'System Health', icon: CheckCircle, type: 'system-health' },
                  { name: 'Throughput', icon: Activity, type: 'throughput' },
                  { name: 'Efficiency Metrics', icon: Zap, type: 'efficiency' },
                  { name: 'Trend Analysis', icon: LineChart, type: 'trend-analysis' }
                ].map((report) => {
                  const Icon = report.icon;
                  return (
                    <Button
                      key={report.type}
                      variant="outline"
                      className="h-32 flex flex-col items-center justify-center gap-3 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      onClick={() => handleGenerateReport(report.type)}
                    >
                      <Icon className="h-8 w-8" />
                      <span className="text-sm font-medium">{report.name}</span>
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* SLA Tab */}
      {activeTab === 'sla' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                SLA Reports
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { name: 'SLA Compliance', icon: CheckCircle, type: 'sla-compliance' },
                  { name: 'Breach Analysis', icon: AlertCircle, type: 'sla-breach' },
                  { name: 'Response Time SLA', icon: Clock, type: 'response-sla' },
                  { name: 'Resolution Time SLA', icon: Target, type: 'resolution-sla' },
                  { name: 'SLA Trends', icon: TrendingUp, type: 'sla-trends' },
                  { name: 'SLA Forecast', icon: LineChart, type: 'sla-forecast' }
                ].map((report) => {
                  const Icon = report.icon;
                  return (
                    <Button
                      key={report.type}
                      variant="outline"
                      className="h-32 flex flex-col items-center justify-center gap-3 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      onClick={() => handleGenerateReport(report.type)}
                    >
                      <Icon className="h-8 w-8" />
                      <span className="text-sm font-medium">{report.name}</span>
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Custom Tab */}
      {activeTab === 'custom' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Custom Reports
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="customReportName">Report Name</Label>
                  <Input
                    id="customReportName"
                    placeholder="Enter report name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customReportType">Report Type</Label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Select report type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tickets">Tickets</SelectItem>
                      <SelectItem value="agents">Agents</SelectItem>
                      <SelectItem value="performance">Performance</SelectItem>
                      <SelectItem value="sla">SLA</SelectItem>
                      <SelectItem value="custom">Custom Query</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="customQuery">Custom Query (SQL)</Label>
                <textarea
                  id="customQuery"
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  rows={8}
                  placeholder="SELECT * FROM tickets WHERE created_at >= '2024-01-01'"
                />
              </div>

              <div className="flex items-center gap-4">
                <Button onClick={() => handleGenerateReport('custom')}>
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Generate Report
                </Button>
                <Button variant="outline">
                  <FileText className="h-4 w-4 mr-2" />
                  Save Template
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Scheduled Tab */}
      {activeTab === 'scheduled' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Scheduled Reports
                </CardTitle>
                <Button onClick={() => setShowScheduleModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Schedule Report
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {scheduledReports.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">No scheduled reports. Schedule your first report to automate reporting.</p>
                  </div>
                ) : (
                  scheduledReports.map((scheduled) => (
                    <div key={scheduled.id} className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <div className="flex items-center gap-4">
                        <Calendar className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">{scheduled.name}</h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Frequency: {scheduled.frequency} | Next Run: {scheduled.nextRun}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={scheduled.status === 'active' ? 'success' : 'secondary'}>
                          {scheduled.status}
                        </Badge>
                        <Button variant="outline" size="sm">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Schedule Report Modal */}
      <Modal 
        isOpen={showScheduleModal} 
        onClose={() => setShowScheduleModal(false)}
        title="Schedule Report"
      >
        <div className="p-6 space-y-4">
          <div>
            <Label htmlFor="scheduleReportName">Report Name</Label>
            <Input 
              id="scheduleReportName" 
              placeholder="Enter report name" 
              defaultValue={scheduleReport?.name || ''}
            />
          </div>
          <div>
            <Label htmlFor="scheduleFrequency">Frequency</Label>
            <Select>
              <SelectTrigger>
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="scheduleEmail">Email Recipients</Label>
            <Input id="scheduleEmail" type="email" placeholder="email@example.com" />
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowScheduleModal(false)}>
              Cancel
            </Button>
            <Button onClick={() => {
              toast.success(`${scheduleReport?.name || 'Report'} scheduled successfully`);
              setShowScheduleModal(false);
              setScheduleReport(null);
            }}>
              Schedule
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isPreviewModalOpen}
        onClose={() => {
          setIsPreviewModalOpen(false);
          setPreviewReport(null);
        }}
        title={previewReport?.name || 'Report Preview'}
      >
        <div className="p-6 space-y-4">
          {previewReport ? (
            <>
              <div className="space-y-2">
                <p className="text-sm text-gray-600 dark:text-gray-300">{previewReport.description}</p>
                <div className="flex flex-wrap gap-3 text-sm text-gray-500 dark:text-gray-400">
                  <span>Status: <strong className="capitalize">{previewReport.status}</strong></span>
                  <span>Type: <strong>{previewReport.type}</strong></span>
                  <span>Created: {previewReport.createdAt ? new Date(previewReport.createdAt).toLocaleString() : 'N/A'}</span>
                  <span>Size: {previewReport.size || 'N/A'} KB</span>
                </div>
              </div>
              <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-4 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/40">
                Preview content placeholder. Integrate real report preview once backend endpoints are available.
              </div>
              <div className="flex flex-wrap gap-2 justify-end pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsPreviewModalOpen(false);
                    setPreviewReport(null);
                  }}
                >
                  Close
                </Button>
                <Button
                  disabled={previewReport.status !== 'completed'}
                  onClick={() => handleDownloadReport(previewReport.id, 'pdf')}
                >
                  Download PDF
                </Button>
                <Button
                  variant="outline"
                  disabled={previewReport.status !== 'completed'}
                  onClick={() => handleDownloadReport(previewReport.id, 'csv')}
                >
                  Download CSV
                </Button>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">Select a report to preview.</p>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Reports;
